import * as yup from 'yup';
import memoizeOne from 'memoize-one';
import numbro from 'numbro';
import NUMBRO_LOCALES from '../../../utils/intl/numbroLocales';

const integerRegex = /^\d+$/;
const postalCodeRegex = /^\d{4,5}?$/;
const phoneRegex =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;
const maxChars = 100;
const maxTextareaChars = 1000;

const MAXIMUM_FILE_SIZE = 1024 * 1024;
export const ALLOWED_FILE_EXTENSIONS = [
  '.xls',
  '.xlsx',
  '.doc',
  '.docx',
  '.pdf',
  '.ppt',
  '.jpg',
  '.png',
  '.bmp',
  '.csv',
];

export const ALLOWED_FILE_EXTENSIONS_WIND = ['.xlsx'];

export function getFileFormatsMessageFromAllowedExtensions(
  allowedExtensions,
  intl
) {
  let fileFormats;
  if (allowedExtensions.length === 1) {
    [fileFormats] = allowedExtensions;
  } else {
    const fileFormats1 = allowedExtensions
      .slice(0, allowedExtensions.length - 2)
      .join(', ');
    const and = intl.formatMessage({ id: 'offerWizard.fileUpload.info1.and' });
    const fileFormats2 = allowedExtensions[allowedExtensions.length - 1];
    fileFormats = `${fileFormats1} ${and} ${fileFormats2}`;
  }
  return fileFormats;
}

const validationSchema = memoizeOne((intl) => {
  const delimiter = numbro.languageData(NUMBRO_LOCALES[intl.locale]).delimiters
    .decimal;
  const numberRegex = new RegExp(`^\\d+([${delimiter}]\\d{1,3})?$`);
  const coordinatesRegex = new RegExp(`^\\d{1,2}[${delimiter}]\\d{3,}$`);

  const onlyNumbers = intl.formatMessage({
    id: 'offerWizard.validation.onlyNumbers',
  });
  const onlyIntegers = intl.formatMessage({
    id: 'offerWizard.validation.onlyIntegers',
  });
  const coordinates = intl.formatMessage({
    id: 'offerWizard.validation.coordinates',
  });
  const inputExceedsMaxChars = intl.formatMessage(
    { id: 'offerWizard.validation.inputTooLong' },
    { maxChars }
  );
  const enterRegelzone = intl.formatMessage({
    id: 'offerWizard.validation.enterRegelzone',
  });
  const enterConsumption = intl.formatMessage({
    id: 'offerWizard.validation.enterConsumption',
  });

  const initialOperationYup = yup.string().nullable();

  const generatedYup = yup
    .string()
    .matches(numberRegex, onlyNumbers)
    .nullable();

  const fileYup = (allowedFileExtensions) =>
    yup
      .mixed()
      .test(
        'fileSize',
        intl.formatMessage({ id: 'offerWizard.validation.fileTooLarge' }),
        (value) => !value || value.size <= MAXIMUM_FILE_SIZE
      )
      .test(
        'name',
        intl.formatMessage({ id: 'offerWizard.validation.invalidFileType' }),
        (value) => {
          if (!value) return true;
          const splittedName = value.name.split('.');
          const extension = `.${splittedName[splittedName.length - 1]}`;
          return allowedFileExtensions.includes(extension.toLowerCase());
        }
      )
      .notRequired();

  const address = {
    street: yup.string().max(maxChars, inputExceedsMaxChars).nullable(),
    zipcode: yup
      .string()
      .matches(
        postalCodeRegex,
        intl.formatMessage({ id: 'offerWizard.validation.invalidZipcode' })
      )
      .nullable(),
    town: yup.string().max(maxChars, inputExceedsMaxChars).nullable(),
    country: yup.object().nullable(),
  };

  const informationWithoutDates = {
    company: yup
      .string()
      .required(
        intl.formatMessage({ id: 'offerWizard.validation.enterCompanyName' })
      )
      .max(maxChars, inputExceedsMaxChars)
      .nullable(),
    street: address.street.required(
      intl.formatMessage({ id: 'offerWizard.validation.enterTheStreet' })
    ),
    zipcode: address.zipcode.required(
      intl.formatMessage({ id: 'offerWizard.validation.enterTheZipcde' })
    ),
    town: address.town.required(
      intl.formatMessage({ id: 'offerWizard.validation.enterTheTown' })
    ),
    country: address.country.required(
      intl.formatMessage({ id: 'offerWizard.validation.enterTheCountry' })
    ),
    notes: yup
      .string()
      .max(
        maxTextareaChars,
        intl.formatMessage(
          { id: 'offerWizard.validation.inputTooLong' },
          { maxChars: maxTextareaChars }
        )
      ),
    file: fileYup(ALLOWED_FILE_EXTENSIONS),
  };
  return yup.object({
    category: yup.string().required(),
    step: yup.number(),
    product: yup.string().when(['step'], {
      is: (step) => step >= 1,
      then: (schema) => schema.required(),
    }),
    facilityType: yup.string().when(['product', 'step'], {
      is: (product, step) => step >= 2 && product === 'direktvermarktung',
      then: (schema) => schema.required(),
    }),
    certificate: yup.string().when(['product', 'step'], {
      is: (product, step) => step >= 2 && product === 'zertifikate',
      then: (schema) => schema.required(),
    }),
    powerType: yup.string().when(['product', 'step'], {
      is: (product, step) => step >= 2 && product === 'stromlieferung',
      then: (schema) => schema.required(),
    }),
    greenPowerData: yup.object().when(['product', 'powerType', 'step'], {
      is: (product, powerType, step) =>
        product === 'stromlieferung' && powerType === 'gruenstrom' && step >= 3,
      then: (schema) =>
        schema.shape({
          wind: yup.boolean(),
          water: yup.boolean(),
          photovoltaic: yup.boolean(),
          biomass: yup.boolean(),
          consumption: yup
            .string()
            .matches(numberRegex, onlyNumbers)
            .required(enterConsumption),
          certification: yup.string(),
          label: yup.string().max(maxChars, inputExceedsMaxChars),
          regionOfOrigin: yup.string(),
          numberOfMeterPoints: yup.string().matches(integerRegex, onlyIntegers),
          controlArea: yup.string(),
          customControlArea: yup.string().when(['controlArea'], {
            is: 'custom',
            then: (customControlAreaSchema) =>
              customControlAreaSchema.required(enterRegelzone),
          }),
          product: yup.string(),
          // Note: this is not an actual field in the form. This is a hack, so that
          // we have an error to look for, when we decide, whether the consumption is large enough to sell
          isGreenPowerConsumptionTooSmall: yup.mixed().when('consumption', {
            is: (consumption) => {
              const parsedConsumption = numbro.unformat(consumption);
              const isConsumptionEmpty = parsedConsumption === undefined;
              return !isConsumptionEmpty && parsedConsumption < 5;
            },
            then: (isGreenPowerConsumptionTooSmallSchema) =>
              isGreenPowerConsumptionTooSmallSchema.test(
                'consumptionTooSmall',
                () => false
              ),
          }),
        }),
    }),
    greyPowerData: yup.object().when(['product', 'powerType', 'step'], {
      is: (product, powerType, step) =>
        product === 'stromlieferung' && powerType === 'graustrom' && step >= 3,
      then: (schema) =>
        schema.shape({
          consumption: yup
            .string()
            .matches(numberRegex, onlyNumbers)
            .required(enterConsumption),
          numberOfMeterPoints: yup.string().matches(integerRegex, onlyIntegers),
          controlArea: yup.string(),
          customControlArea: yup.string().when(['controlArea'], {
            is: 'custom',
            then: (customControlAreaSchema) =>
              customControlAreaSchema.required(enterRegelzone),
          }),
          product: yup.string(),
          // Note: this is not an actual field in the form. This is a hack, so that
          // we have an error to look for, when we decide, whether the consumption is large enough to sell
          isGreyPowerConsumptionTooSmall: yup.mixed().when('consumption', {
            is: (consumption) => {
              const parsedConsumption = numbro.unformat(consumption);
              const isConsumptionEmpty = parsedConsumption === undefined;
              return !isConsumptionEmpty && parsedConsumption < 5;
            },
            then: (isGreyPowerConsumptionTooSmallSchema) =>
              isGreyPowerConsumptionTooSmallSchema.test(
                'consumptionTooSmall',
                () => false
              ),
          }),
        }),
    }),
    windFacilityData: yup
      .array()
      .when(['product', 'facilityType', 'step', 'windFacilityFile'], {
        is: (product, facilityType, step, windFacilityFile) =>
          product === 'direktvermarktung' &&
          facilityType === 'wind' &&
          step >= 3 &&
          !windFacilityFile,
        then: (schema) =>
          schema.of(
            yup.object().shape({
              ...address,
              hubHeight: yup
                .string()
                .matches(numberRegex, onlyNumbers)
                .nullable(),
              diameter: yup
                .string()
                .matches(numberRegex, onlyNumbers)
                .nullable(),
              output: yup.string().matches(numberRegex, onlyNumbers).nullable(),
              powerDensity: yup
                .string()
                .matches(numberRegex, onlyNumbers)
                .nullable(),
              manufacturer: yup
                .string()
                .max(maxChars, inputExceedsMaxChars)
                .nullable(),
              model: yup
                .string()
                .max(maxChars, inputExceedsMaxChars)
                .nullable(),
              initialOperation: initialOperationYup,
              latitude: yup
                .string()
                .matches(coordinatesRegex, coordinates)
                .nullable(),
              longitude: yup
                .string()
                .matches(coordinatesRegex, coordinates)
                .nullable(),
              controlArea: yup.string(),
              customControlArea: yup.string().when(['controlArea'], {
                is: 'custom',
                then: (customControlAreaSchema) =>
                  customControlAreaSchema.required(enterRegelzone),
              }),
            })
          ),
      }),
    windFacilityFile: yup.mixed().when(['product', 'facilityType', 'step'], {
      is: (product, facilityType, step) =>
        product === 'direktvermarktung' && facilityType === 'wind' && step >= 3,
      then: () => fileYup(ALLOWED_FILE_EXTENSIONS_WIND),
    }),
    waterFacilityData: yup.array().when(['product', 'facilityType', 'step'], {
      is: (product, facilityType, step) =>
        product === 'direktvermarktung' &&
        facilityType === 'wasser' &&
        step >= 3,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            ...address,
            output: yup.string().matches(numberRegex, onlyNumbers).nullable(),
            generated: yup
              .string()
              .matches(numberRegex, onlyNumbers)
              .nullable(),
            initialOperation: initialOperationYup,
            latitude: yup
              .string()
              .matches(coordinatesRegex, coordinates)
              .nullable(),
            longitude: yup
              .string()
              .matches(coordinatesRegex, coordinates)
              .nullable(),
          })
        ),
    }),
    solarFacilityData: yup.array().when(['product', 'facilityType', 'step'], {
      is: (product, facilityType, step) =>
        product === 'direktvermarktung' &&
        facilityType === 'photovoltaik' &&
        step >= 3,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            ...address,
            output: yup.string().matches(numberRegex, onlyNumbers).nullable(),
            generated: yup
              .string()
              .matches(numberRegex, onlyNumbers)
              .nullable(),
            initialOperation: initialOperationYup,
            latitude: yup
              .string()
              .matches(coordinatesRegex, coordinates)
              .nullable(),
            longitude: yup
              .string()
              .matches(coordinatesRegex, coordinates)
              .nullable(),
          })
        ),
    }),
    bioMasseFacilityData: yup
      .array()
      .when(['product', 'facilityType', 'step'], {
        is: (product, facilityType, step) =>
          product === 'direktvermarktung' &&
          facilityType === 'biomasse' &&
          step >= 3,
        then: (schema) =>
          schema.of(
            yup.object().shape({
              ...address,
              generated: generatedYup,
              initialOperation: initialOperationYup,
              latitude: yup
                .string()
                .matches(coordinatesRegex, coordinates)
                .nullable(),
              longitude: yup
                .string()
                .matches(coordinatesRegex, coordinates)
                .nullable(),
            })
          ),
      }),
    co2CertificateData: yup.object().when(['product', 'certificate', 'step'], {
      is: (product, certificate, step) =>
        product === 'zertifikate' && certificate === 'co2' && step >= 3,
      then: (schema) =>
        schema.shape({
          consumption: yup
            .string()
            .matches(numberRegex, onlyNumbers)
            .nullable(),
          kindOfCertificate: yup.string(),
          kindOfContract: yup.string().when(['kindOfCertificate'], {
            is: 'eua',
            then: yup
              .string()
              .ensure()
              .required(
                intl.formatMessage({
                  id: 'offerWizard.validation.selectContractType',
                })
              ),
          }),
        }),
    }),
    proofOfOriginCertificateData: yup
      .object()
      .when(['product', 'certificate', 'step'], {
        is: (product, certificate, step) =>
          product === 'zertifikate' &&
          certificate === 'proofOfOrigin' &&
          step >= 3,
        then: (schema) =>
          schema.shape({
            consumption: yup
              .string()
              .matches(numberRegex, onlyNumbers)
              .nullable(),
            certification: yup.string(),
            origin: yup.string(),
          }),
      }),
    largeSolarFacilityType: yup.string().when(['product', 'step'], {
      is: (product, step) => product === 'pvGrossanlage' && step >= 2,
      then: (schema) => schema.required(),
    }),
    largeSolarFacilityData: yup.object().when(['product', 'step'], {
      is: (product, step) => product === 'pvGrossanlage' && step >= 3,
      then: (schema) =>
        schema.shape(
          {
            sizeOfArea: yup
              .string()
              .nullable()
              .matches(numberRegex, onlyNumbers)
              // Make sure one of the fields has a value
              .when('consumption', {
                is: (consumptionValue) =>
                  consumptionValue === '' || consumptionValue === undefined,
                then: (sizeOfAreaSchema) =>
                  sizeOfAreaSchema
                    .matches(numberRegex, onlyNumbers)
                    .nullable()
                    .required(
                      intl.formatMessage({
                        id: 'offerWizard.validation.largeSolarFacility.oneEntryRequired',
                      })
                    ),
              }),
            consumption: yup
              .string()
              .nullable()
              .matches(numberRegex, onlyNumbers)
              // Make sure one of the fields has a value
              .when('sizeOfArea', {
                is: (sizeOfAreaValue) =>
                  sizeOfAreaValue === '' || sizeOfAreaValue === undefined,
                then: (consumptionSchema) =>
                  consumptionSchema
                    .matches(numberRegex, onlyNumbers)
                    .nullable()
                    .required(
                      intl.formatMessage({
                        id: 'offerWizard.validation.largeSolarFacility.oneEntryRequired',
                      })
                    ),
              }),
            // Note: this is not an actual field in the form. This is a hack, so that
            // we have an error to look for, when we decide, whether the client's area or
            // consumption is large enough to sell them a solar facility (small facilities
            // are sold out).
            isSolarFacilitySoldOut: yup
              .mixed()
              .when(['consumption', 'sizeOfArea'], {
                is: (consumption, sizeOfArea) => {
                  const parsedConsumption = numbro.unformat(consumption);
                  const isConsumptionEmpty = parsedConsumption === undefined;
                  const parsedSizeOfArea = numbro.unformat(sizeOfArea);
                  const isSizeOfAreaEmpty = parsedSizeOfArea === undefined;
                  return (
                    // only consumption filled out and is too small
                    (isSizeOfAreaEmpty &&
                      !isConsumptionEmpty &&
                      parsedConsumption < 5000) ||
                    // only area filled out and is too small
                    (isConsumptionEmpty &&
                      !isSizeOfAreaEmpty &&
                      parsedSizeOfArea < 5000) ||
                    // both filled out and both too small
                    (!isConsumptionEmpty &&
                      !isSizeOfAreaEmpty &&
                      parsedSizeOfArea < 5000 &&
                      parsedConsumption < 5000)
                  );
                },
                then: (isSolarFacilitySoldOutSchema) =>
                  isSolarFacilitySoldOutSchema.test(
                    'isEitherConsumptionOrAreaLargeEnough',
                    'facilityTooSmall',
                    () => false
                  ),
              }),
          },
          [['consumption', 'sizeOfArea']]
        ),
    }),
    businessChargingData: yup.object().when(['product', 'step'], {
      is: (product, step) => product === 'businessCharging' && step >= 3,
      then: (schema) =>
        schema.shape({
          numberOfEmployees: yup.string().matches(integerRegex, onlyIntegers),
          numberOfParkingSpaces: yup
            .string()
            .matches(integerRegex, onlyIntegers),
          numberOfOwnVehicles: yup.string().matches(integerRegex, onlyIntegers),
          numberOfElectricCars: yup
            .string()
            .matches(integerRegex, onlyIntegers),
        }),
    }),
    immoChargingData: yup.object().when(['product', 'step'], {
      is: (product, step) => product === 'immoCharging' && step >= 2,
      then: (schema) =>
        schema.shape(
          {
            isResidentialOrCommercial: yup.string().required(
              intl.formatMessage({
                id: 'offerWizard.validation.immoCharging.enterTypeOfImmoCharging',
              })
            ),
            numberOfUnits: yup
              .string()
              .nullable()
              .matches(numberRegex, onlyNumbers)
              .required(
                intl.formatMessage({
                  id: 'offerWizard.validation.immoCharging.enterNumberOfUnits',
                })
              ),
            numberOfGarageObjects: yup
              .string()
              .nullable()
              .matches(numberRegex, onlyNumbers)
              .required(
                intl.formatMessage({
                  id: 'offerWizard.validation.immoCharging.enterNumberOfGarageObjects',
                })
              ),
            numberOfParkingSpaces: yup
              .string()
              .nullable()
              .matches(numberRegex, onlyNumbers),
          },
          [['numberOfUnits', 'numberOfGarageObjects']]
        ),
    }),
    coopChargingData: yup.object().when(['product', 'step'], {
      is: (product, step) => product === 'coopCharging' && step >= 2,
      then: (schema) =>
        schema.shape({
          numberOfHotelLocations: yup
            .string()
            .nullable()
            .matches(numberRegex, onlyNumbers)
            .required(
              intl.formatMessage({
                id: 'offerWizard.validation.coopCharging.numberOfHotelLocations',
              })
            ),
          numberOfRooms: yup
            .string()
            .nullable()
            .matches(numberRegex, onlyNumbers)
            .required(
              intl.formatMessage({
                id: 'offerWizard.validation.coopCharging.numberOfRooms',
              })
            ),
          numberOfParkingSpaces: yup
            .string()
            .nullable()
            .matches(numberRegex, onlyNumbers),
        }),
    }),
    information: yup
      .object()
      .when(['product', 'step'], {
        // dates are not needed when product is large solar plant or business charging
        is: (product, step) =>
          step >= 3 &&
          product !== 'pvGrossanlage' &&
          product !== 'businessCharging',
        then: (schema) =>
          schema.shape({
            start: yup
              .date()
              .min(
                new Date(),
                intl.formatMessage({
                  id: 'offerWizard.validation.dateCantBeInPast',
                })
              )
              .nullable(),
            end: yup
              .date()
              .min(
                yup.ref('start'),
                intl.formatMessage({
                  id: 'offerWizard.validation.endCantBeBeforeStartDate',
                })
              )
              .when('start', ([start], endDateSchema) => {
                if (!start) {
                  return endDateSchema;
                }
                const latestAllowedDate = new Date(start);
                latestAllowedDate.setFullYear(
                  latestAllowedDate.getFullYear() + 5
                );
                return endDateSchema.max(
                  latestAllowedDate,
                  intl.formatMessage({
                    id: 'offerWizard.validation.timeframeCantExceed5Years',
                  })
                );
              })
              .nullable(),
          }),
      })
      .when(['step'], {
        is: (step) => step >= 4,
        then: (schema) =>
          schema.shape({
            ...informationWithoutDates,
          }),
      }),
    individualOffer: yup.object().when(['step'], {
      is: (step) => step >= 5,
      then: (schema) =>
        schema.shape({
          salutation: yup
            .object()
            .nullable()
            .required(
              intl.formatMessage({
                id: 'offerWizard.validation.enterSalutation',
              })
            ),
          firstName: yup
            .string()
            .required(
              intl.formatMessage({
                id: 'offerWizard.validation.enterFirstName',
              })
            )
            .max(maxChars, inputExceedsMaxChars)
            .nullable(),
          lastName: yup
            .string()
            .required(
              intl.formatMessage({
                id: 'offerWizard.validation.enterLastName',
              })
            )
            .max(maxChars, inputExceedsMaxChars)
            .nullable(),
          position: yup.string().max(maxChars, inputExceedsMaxChars).nullable(),
          phone: yup
            .string()
            .matches(
              phoneRegex,
              intl.formatMessage({
                id: 'offerWizard.validation.enterPhoneNumber',
              })
            )
            .max(maxChars, inputExceedsMaxChars)
            .nullable(),
          email: yup
            .string()
            .required(
              intl.formatMessage({ id: 'offerWizard.validation.enterEmail' })
            )
            .email(
              intl.formatMessage({ id: 'offerWizard.validation.invalidEmail' })
            )
            .max(maxChars, inputExceedsMaxChars)
            .nullable(),
          privacyPolicy: yup
            .boolean()
            .oneOf(
              [true],
              intl.formatMessage({ id: 'offerWizard.validation.consentNeeded' })
            ),
        }),
    }),
  });
});

export default validationSchema;
